import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import App from './App';
import theme from './theme';
import { registerServiceWorker } from './serviceWorkerRegistration';
import { AuthProvider } from './contexts/AuthContext';
// Outermost error boundary: a crash in any provider or page renders a readable message
// instead of an empty (previously teal) page that gives nobody a clue what broke.
import ErrorBoundary from './components/UI/ErrorBoundary';
// Switched to queue-based notification context (provides useNotify)
import { NotificationProvider } from './contexts/NotificationContext';
import { RateLimitProvider } from './contexts/RateLimitContext';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorBoundary
            title="This screen failed to render"
            message="The rest of the app is unaffected and nothing was saved or sent. Reload to try again. If you just deployed, hard-refresh (Ctrl+F5) to drop the cached bundle."
          >
            <NotificationProvider>
              <RateLimitProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </RateLimitProvider>
            </NotificationProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </BrowserRouter>

    </QueryClientProvider>
  </React.StrictMode>
);

// Keep the content-hashed bundle on disk so a second visit does not re-download
// it across the lossy production link (see client/public/service-worker.js).
// `registerServiceWorker` is a no-op outside production builds, so the dev
// server's hot reload is untouched.
registerServiceWorker();
